import React, { ReactElement } from 'react';
import { ListRenderer } from '@feast-it/ui/light';

import {
  PillButton,
  PillButtonContainer,
} from '../../../components/PillButton';
import { PageProperties, SeoEventType } from '../../../types';
import { trackEvent } from '../../../modules/analytics';
import { EVENT_TYPE_CLICKED } from '../../../constants/analytics';
import { useRouter } from 'next/router';
import { getAbsoluteUrl } from '@/helpers/formatUrl';
import { SliceLayout } from '../../../components/SliceLayout';
import { Container, StyledHeading } from './EventTypes.styled';

interface EventTypeProps {
  pageProperties: PageProperties;
}

export function EventTypes({ pageProperties }: EventTypeProps): ReactElement {
  const router = useRouter();
  const { pageContent, inLocation, inDefaultLocation } = pageProperties;
  const { seoVertical, eventTypes, seoTag } = pageContent;

  const buildPath = (eventTypePath: string) => {
    const currentUrl = pageProperties.canonicalPath;
    const path = eventTypePath.toLowerCase();

    // If this page already targets a specific SEO tag, replace that segment
    if (seoTag) {
      return currentUrl.replace(
        seoTag.toLowerCase().replace(/\s+/g, '-'),
        path
      );
    }

    // Otherwise, append the event-type path to the canonical URL
    return `${currentUrl}/${path}`;
  };

  const manualEventTypesData = [
    {
      name: 'Wedding',
      icon: 'Wedding',
      path: 'wedding',
    },
    {
      name: 'Corporate event',
      icon: 'Corporate',
      path: 'corporate-event',
    },
    {
      name: 'Birthday',
      icon: 'Birthday',
      path: 'birthday',
    },
    {
      name: 'Dinner party',
      icon: 'DinnerParty',
      path: 'dinner-party',
    },
    {
      name: 'Christmas party',
      icon: 'Celebration',
      path: 'christmas-party',
    },
    {
      name: 'Party',
      icon: 'Celebration',
      path: 'party',
    },
    {
      name: 'Summer party',
      icon: 'Celebration',
      path: 'summer-party',
    },
    {
      name: 'Event',
      icon: 'Celebration',
      path: 'event',
    },
  ];

  // Override the event types shown for specific verticals
  const overrideEventTypes: Record<string, string[]> = {
    catering: ['Wedding', 'Corporate event', 'Dinner party', 'Birthday'],
    venues: [
      'Corporate event',
      'Christmas party',
      'Wedding',
      'Party',
      'Summer party',
    ],
    'mobile-bars': ['Wedding', 'Corporate event', 'Birthday'],
    photographers: ['Corporate event', 'Wedding', 'Event'],
  };

  const desiredEventTypes = overrideEventTypes[seoVertical.toLowerCase()];
  const displayEventTypes = React.useMemo(() => {
    if (!desiredEventTypes) return eventTypes || [];

    const findEventType = (name: string) => {
      return manualEventTypesData.find(
        et =>
          et.name.toLowerCase() === name.toLowerCase() &&
          et.name.toLowerCase() !== seoTag.toLowerCase()
      );
    };

    const filtered = desiredEventTypes
      .map(findEventType)
      .filter((et): et is NonNullable<typeof et> => Boolean(et));

    // If we couldn't match any, fallback to whichever list we have
    return filtered.length ? filtered : eventTypes || manualEventTypesData;
  }, [desiredEventTypes, eventTypes, manualEventTypesData, seoTag]);

  return (
    <SliceLayout backgroundColor="white">
      <Container>
        <StyledHeading as="h2" style="headingMobileExtraLarge" color="black">
          {getHeading(seoVertical, inLocation, inDefaultLocation)}
        </StyledHeading>

        <PillButtonContainer>
          <ListRenderer
            items={displayEventTypes as SeoEventType[]}
            render={({ item }) => {
              const href = desiredEventTypes
                ? buildPath(item.path || '')
                : item.path;

              return (
                <PillButton
                  icon={item.icon}
                  href={href}
                  title={item.name}
                  seoVertical={seoVertical}
                  inLocation={inLocation}
                  onClick={() =>
                    trackEvent(EVENT_TYPE_CLICKED, {
                      source: 'seo popular events',
                      event_type: item.name,
                      destinationUrl: `${getAbsoluteUrl(href)}`,
                      currentPage: getAbsoluteUrl(router.asPath),
                    })
                  }
                />
              );
            }}
          />
        </PillButtonContainer>
      </Container>
    </SliceLayout>
  );
}

const getHeading = (
  seoVertical: string,
  inLocation: string,
  inDefaultLocation: string
) => {
  return seoVertical.toLowerCase() !== 'venues'
    ? `Our most popular event types ${inLocation || inDefaultLocation}`
    : 'Venue hire for all occasions';
};
